import axios from 'axios';

// Configuração base do axios
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_API_URL || 'https://reportfius-backend.up.railway.app'
  : 'http://localhost:3001';

if (process.env.NODE_ENV !== 'production') {
  console.log('🔧 formsApi: NODE_ENV:', process.env.NODE_ENV);
  console.log('🔧 formsApi: API_BASE_URL:', API_BASE_URL);
  console.log('🔧 formsApi: REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
}

// API com autenticação para admin
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000, // Aumentado para 30 segundos
  withCredentials: true
});

// Interceptor para adicionar token automaticamente
api.interceptors.request.use((config) => {
  if (config?.headers) {
    delete config.headers.Authorization;
    delete config.headers.authorization;
    // Header custom exigido pela proteção CSRF do backend em toda escrita.
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
  }
  return config;
});

// Interceptor para tratar erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// API sem autenticação para formulários públicos
const publicApi = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000, // Aumentado para 30 segundos
  withCredentials: true
});

// Header custom exigido pela proteção CSRF do backend em toda escrita — é o
// publicApi que envia a DENÚNCIA (/complaints/submit); sem isto ela toma 403.
publicApi.interceptors.request.use((config) => {
  if (config?.headers) config.headers['X-Requested-With'] = 'XMLHttpRequest';
  return config;
});

// Interceptor para tratar erros em formulários públicos
publicApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Não redirecionar para login em formulários públicos
    return Promise.reject(error);
  }
);

// ====== SERVIÇO DE FORMULÁRIOS ======

export const formsApi = {
  // Listar todos os formulários
  async getForms() {
    console.log('📝 Buscando formulários...');
    const response = await api.get('/forms');
    console.log('✅ Resposta dos formulários:', response.data);
    return response.data;
  },

  // Buscar formulário específico
  async getForm(id) {
    const response = await api.get(`/forms/${id}`);
    return response.data;
  },

  // Criar novo formulário
  async createForm(formData) {
    const response = await api.post('/forms', formData, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  },

  // Atualizar formulário
  async updateForm(id, formData) {
    const response = await api.put(`/forms/${id}`, formData, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  },

  // Deletar formulário
  async deleteForm(id) {
    const response = await api.delete(`/forms/${id}`);
    return response.data;
  },

  // Alternar status do formulário
  async toggleFormStatus(id) {
    const response = await api.patch(`/forms/${id}/toggle`);
    return response.data;
  }
};

// ====== SERVIÇO DE DENÚNCIAS ======

export const complaintsApi = {
  // Listar denúncias
  async getComplaints(params = {}) {
    const response = await api.get('/complaints', { params });
    return response.data;
  },

  // Buscar denúncia específica
  async getComplaint(id) {
    const response = await api.get(`/complaints/${id}`);
    return response.data;
  },

  // Criar nova denúncia
  async createComplaint(complaintData) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('📝 complaintsApi.createComplaint: Enviando dados:', complaintData);
    }
    // Verificar se há arquivos para enviar
    const hasFiles = Object.values(complaintData.formData).some(value => value instanceof File);
    if (hasFiles) {
      // Checar tamanho dos arquivos antes de enviar
      Object.values(complaintData.formData).forEach(value => {
        if (value instanceof File && value.size > 5 * 1024 * 1024) {
          alert('O arquivo selecionado ultrapassa o limite de 5MB e não pode ser enviado. Por favor, escolha um arquivo menor.');
          throw new Error('Arquivo muito grande');
        }
      });
      // Se há arquivos, usar FormData
      const formData = new FormData();
      // Adicionar dados básicos
      formData.append('formId', complaintData.formId);
      formData.append('isAnonymous', complaintData.isAnonymous);
      if (complaintData.anonymousUser) {
        formData.append('anonymousUser', JSON.stringify(complaintData.anonymousUser));
      }
      if (complaintData.identifiedUser) {
        formData.append('identifiedUser', JSON.stringify(complaintData.identifiedUser));
      }
      // Adicionar todos os campos do formulário, inclusive arquivos, preservando o nome do campo
      Object.keys(complaintData.formData).forEach(key => {
        const value = complaintData.formData[key];
        if (value !== null && value !== undefined) {
          formData.append(`formData[${key}]`, value);
        }
      });
      // Remover todos os logs de debug relacionados ao envio de FormData
      try {
        const response = await publicApi.post('/complaints/submit', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (process.env.NODE_ENV !== 'production') {
          console.log('✅ complaintsApi.createComplaint: Resposta recebida:', response.data);
        }
        return response.data;
      } catch (error) {
        if (error.response) {
          // Erro do servidor
          return {
            message: error.response.data?.message || 'Erro do servidor',
            status: error.response.status,
            data: error.response.data
          };
        } else if (error.request) {
          // Erro de rede
          return {
            message: 'Erro de conexão. Verifique sua internet.',
            status: 0
          };
        } else {
          // Erro geral
          return {
            message: error.message || 'Erro desconhecido',
            status: 0
          };
        }
      }
    } else {
      // Se não há arquivos, enviar como JSON
      const response = await publicApi.post('/complaints/submit', complaintData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ complaintsApi.createComplaint: Resposta recebida:', response.data);
      }
      return response.data;
    }
  },

  // Atualizar denúncia
  async updateComplaint(id, complaintData) {
    const response = await api.put(`/complaints/${id}`, complaintData, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  },

  // Deletar denúncia
  async deleteComplaint(id) {
    const response = await api.delete(`/complaints/${id}`);
    return response.data;
  },

  // ====== SISTEMA DE APROVAÇÃO DE DENÚNCIAS ======
  
  // Listar denúncias pendentes de aprovação
  async getPendingApprovals({ retryFailed = false } = {}) {
    const params = retryFailed ? { retry_failed: 'true' } : {};
    const response = await api.get('/complaints/pending-approval', { params });
    return response.data;
  },

  // Editar denúncia pendente
  async updatePendingComplaint(id, data) {
    const response = await api.put(`/complaints/pending-approval/${id}`, data);
    return response.data;
  },

  // Aprovar denúncia
  async approveComplaint(id, data = {}) {
    const response = await api.post(`/complaints/pending-approval/${id}/approve`, data);
    return response.data;
  },

  async reanalyzeTriage(id) {
    const response = await api.post(`/complaints/pending-approval/${id}/reanalyze-triage`);
    return response.data;
  },

  // Buscar formulário público
  async getPublicForm(clientSlug) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 complaintsApi.getPublicForm: Iniciando requisição para:', clientSlug);
      console.log('🔍 complaintsApi.getPublicForm: URL completa:', `${API_BASE_URL}/api/complaints/form/${clientSlug}`);
    }
    const response = await publicApi.get(`/complaints/form/${clientSlug}`);
    if (process.env.NODE_ENV !== 'production') {
      console.log('✅ complaintsApi.getPublicForm: Resposta recebida:', response.data);
    }
    return response.data;
  },

  // Submeter denúncia pública via chatbot ou formulário
  async submitPublicComplaint(clientSlug, formData) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('📝 complaintsApi.submitPublicComplaint: Enviando para:', clientSlug);
    }
    
    try {
      const response = await publicApi.post(`/complaints/submit/${clientSlug}`, formData, {
        headers: { 
          'Content-Type': formData instanceof FormData ? 'multipart/form-data' : 'application/json' 
        }
      });
      
      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ complaintsApi.submitPublicComplaint: Resposta:', response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao submeter denúncia:', error);
      throw error;
    }
  }
};

// ====== SERVIÇO DE AUTENTICAÇÃO ======

export const authApi = {
  // Login de admin
  async adminLogin(credentials) {
    const response = await api.post('/auth/admin/login', credentials);
    return response.data;
  },

  // Login de cliente admin
  async clientAdminLogin(credentials) {
    const response = await api.post('/auth/client-admin/login', credentials);
    return response.data;
  },

  // Logout
  async logout() {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  // Verificar token
  async verifyToken() {
    const response = await api.get('/auth/verify');
    return response.data;
  }
};

// ====== SERVIÇO DE USUÁRIOS ======

export const usersApi = {
  // Listar usuários
  async getUsers() {
    const response = await api.get('/users');
    return response.data;
  },

  // Criar usuário
  async createUser(userData) {
    const response = await api.post('/users', userData);
    return response.data;
  },

  // Atualizar usuário
  async updateUser(id, userData) {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  // Deletar usuário
  async deleteUser(id) {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  }
};

// ====== SERVIÇO DE AUDITORIA ======

export const auditApi = {
  // Listar logs de auditoria
  async getAuditLogs(params = {}) {
    const response = await api.get('/audit', { params });
    return response.data;
  },

  // Buscar log específico
  async getAuditLog(id) {
    const response = await api.get(`/audit/${id}`);
    return response.data;
  }
};

// ====== SERVIÇO DE POWER BI ======

export const powerBiApi = {
  // Buscar dados para Power BI
  async getPowerBiData(params = {}) {
    const response = await api.get('/powerbi/data', { params });
    return response.data;
  },

  // Buscar configurações do Power BI
  async getPowerBiConfig() {
    const response = await api.get('/powerbi/config');
    return response.data;
  }
};

// ====== UTILITÁRIOS ======

export const apiUtils = {
  // Tratar erros de API
  handleError(error) {
    if (error.response) {
      // Erro do servidor
      return {
        message: error.response.data?.message || 'Erro do servidor',
        status: error.response.status,
        data: error.response.data
      };
    } else if (error.request) {
      // Erro de rede
      return {
        message: 'Erro de conexão. Verifique sua internet.',
        status: 0
      };
    } else {
      // Erro geral
      return {
        message: error.message || 'Erro desconhecido',
        status: 0
      };
    }
  },

  // Validar resposta da API
  validateResponse(response) {
    if (!response.success) {
      throw new Error(response.message || 'Erro na resposta da API');
    }
    return response;
  },

  // Formatar dados para envio
  formatFormData(data) {
    return Object.keys(data).reduce((acc, key) => {
      const value = data[key];
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }
};

export default {
  forms: formsApi,
  complaints: complaintsApi,
  auth: authApi,
  users: usersApi,
  audit: auditApi,
  powerBi: powerBiApi,
  utils: apiUtils
}; 
